import React from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { Loader2, Search, Sparkles, Trash2, CheckCircle2, AlertCircle, X, HelpCircle } from 'lucide-react';

/* ============================================================
   Modal "Optimize Storan" — gaya v3.9
   ------------------------------------------------------------
   Warna diambil daripada token --s-* (sistem reka bentuk v3.9).
   Setiap token diberi NILAI SANDARAN tema terang, sebab modal ini
   turut muncul dalam UI lama di mana `body.s-v39` TIDAK dipasang —
   di situ token tidak wujud, jadi sandaran yang mengambil alih.
     · v3.9 gelap  → kad gelap
     · v3.9 terang → kad putih
     · UI lama     → sandaran terang (sepadan halaman lama)
   Butang ditulis dengan gaya sebaris (bukan kelas .s-btn) atas
   sebab yang sama — .s-btn bergantung penuh pada token.
   ============================================================ */
const StorageCleanupOverlay = ({ progress, onConfirm, onClose }) => {
    if (!progress) return null;

    const { stage, message, current = 0, total = 0, reclaimedMB, orphanCount, potentialReclaimMB } = progress;
    const isFinal = stage === 'done' || stage === 'error' || stage === 'nothing';
    const isConfirm = stage === 'confirm';
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;

    /* color = warna aksen padu · soft = isian pudar (latar cip ikon / kotak) */
    const stageMeta = {
        scanning:  { Icon: Search,       title: 'Mengimbas Produk',       color: 'var(--s-brand,#7C3AED)', soft: 'var(--s-brand-soft,rgba(124,58,237,.10))', spin: true },
        finding:   { Icon: Loader2,      title: 'Mencari Fail Terbiar',   color: 'var(--s-brand,#7C3AED)', soft: 'var(--s-brand-soft,rgba(124,58,237,.10))', spin: true },
        confirm:   { Icon: HelpCircle,   title: 'Sahkan Pembersihan',     color: 'var(--s-warn,#D97706)',  soft: 'var(--s-warn-soft,rgba(217,119,6,.12))',   spin: false },
        deleting:  { Icon: Trash2,       title: 'Memadam Fail',           color: 'var(--s-stop,#DC2626)',  soft: 'var(--s-stop-soft,rgba(220,38,38,.12))',   spin: false },
        done:      { Icon: CheckCircle2, title: 'Selesai',                color: 'var(--s-ok,#16A34A)',    soft: 'var(--s-ok-soft,rgba(22,163,74,.12))',     spin: false },
        nothing:   { Icon: Sparkles,     title: 'Storan Sudah Optimum',   color: 'var(--s-ok,#16A34A)',    soft: 'var(--s-ok-soft,rgba(22,163,74,.12))',     spin: false },
        error:     { Icon: AlertCircle,  title: 'Ralat Berlaku',          color: 'var(--s-stop,#DC2626)',  soft: 'var(--s-stop-soft,rgba(220,38,38,.12))',   spin: false },
    }[stage] || { Icon: Loader2, title: 'Memproses…', color: 'var(--s-brand,#7C3AED)', soft: 'var(--s-brand-soft,rgba(124,58,237,.10))', spin: true };

    const { Icon, title, color, soft, spin } = stageMeta;

    const INK   = 'var(--s-ink,#262626)';
    const INK2  = 'var(--s-ink-2,#696969)';
    const INK3  = 'var(--s-ink-3,#9B9B9B)';
    const LINE  = 'var(--s-line,rgba(0,0,0,.08))';

    const btnBase = {
        height: 34, padding: '0 14px', borderRadius: 'var(--s-r,10px)',
        fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        border: '1px solid transparent', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', gap: 6,
        transition: 'opacity .15s ease',
    };
    const btnPrimary = { ...btnBase, background: 'var(--s-primary,#262626)', color: 'var(--s-primary-ink,#FAFAFA)' };
    const btnQuiet   = { ...btnBase, background: 'transparent', color: INK, borderColor: 'var(--s-line-strong,rgba(0,0,0,.14))' };
    /* Destructif: merah PADU tetap (#DC2626) dalam kedua-dua tema. Token
       --s-stop DICERAHKAN kepada #F87171 dalam mod gelap supaya terbaca
       sebagai TEKS di atas latar gelap — tetapi sebagai LATAR butang
       dengan teks putih, merah cerah itu kontras rendah. Butang bahaya
       kekal merah dalam kedua-dua tema, jadi warna tetap betul di sini. */
    const btnStop    = { ...btnBase, background: '#DC2626', color: '#fff' };

    return (
        <AnimatePresence>
            <div
                onClick={(isFinal || isConfirm) ? onClose : undefined}
                style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    display: 'grid', placeItems: 'center', padding: 16,
                    background: 'rgba(0,0,0,.6)',
                    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                }}
            >
                <Motion.div
                    onClick={e => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.96, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 16 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    style={{
                        width: '100%', maxWidth: 416, overflow: 'hidden',
                        background: 'var(--s-card,#FFFFFF)',
                        border: `1px solid ${LINE}`,
                        borderRadius: 'var(--s-r-lg,16px)',
                        boxShadow: 'var(--s-shadow-modal,0 24px 64px -20px rgba(0,0,0,.25))',
                        fontFamily: 'var(--s-sans, inherit)',
                    }}
                >
                    {/* ===== Kandungan ===== */}
                    <div style={{ padding: '22px 22px 18px' }}>
                        {/* Kepala: cip ikon + tajuk + tutup */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: message ? 14 : 0 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 12, flex: 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: soft,
                            }}>
                                <Icon
                                    size={20}
                                    style={{ color, animation: spin ? 'sco-spin 1s linear infinite' : undefined }}
                                />
                            </div>
                            <h2 style={{ margin: 0, flex: 1, fontSize: 16, fontWeight: 700, color: INK, letterSpacing: '-0.01em' }}>
                                {title}
                            </h2>
                            {(isFinal || isConfirm) && (
                                <button
                                    onClick={onClose}
                                    aria-label="Tutup"
                                    style={{
                                        border: 'none', background: 'transparent', cursor: 'pointer',
                                        padding: 4, borderRadius: 8, display: 'flex', color: INK3,
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {/* Mesej */}
                        {message && (
                            <p style={{ margin: 0, fontSize: 13.5, color: INK2, lineHeight: 1.55 }}>
                                {message}
                            </p>
                        )}

                        {/* Peringkat sahkan: ringkasan + amaran */}
                        {isConfirm && (
                            <>
                                <p style={{ margin: '12px 0 0', fontSize: 13.5, color: INK2, lineHeight: 1.55 }}>
                                    Sistem menemui <strong style={{ color: INK }}>{orphanCount}</strong> fail terbiar
                                    (baki automation lama / produk yang telah dipadam).
                                </p>
                                <div style={{
                                    marginTop: 14, padding: '11px 13px', borderRadius: 'var(--s-r,10px)',
                                    background: soft,
                                }}>
                                    <div style={{ fontSize: 13.5, color: INK, fontWeight: 600 }}>
                                        Ruang yang akan dibebaskan: {potentialReclaimMB} MB
                                    </div>
                                    <div style={{ fontSize: 12, marginTop: 3, color }}>
                                        Tindakan ini kekal dan tidak boleh di-undo.
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Bar kemajuan (peringkat memadam) */}
                        {stage === 'deleting' && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginBottom: 8, fontSize: 12.5, fontWeight: 600, color: INK2,
                                }}>
                                    <span>{current} / {total} fail</span>
                                    <span style={{ color }}>{pct}%</span>
                                </div>
                                <div style={{
                                    width: '100%', height: 8, borderRadius: 999,
                                    background: 'var(--s-fill-hover,rgba(0,0,0,.07))', overflow: 'hidden',
                                }}>
                                    <div style={{
                                        width: `${pct}%`, height: '100%', background: color,
                                        borderRadius: 999, transition: 'width 0.3s ease-out',
                                    }} />
                                </div>
                            </div>
                        )}

                        {/* Bar tak tentu (mengimbas / mencari) */}
                        {(stage === 'scanning' || stage === 'finding') && (
                            <div style={{
                                width: '100%', height: 5, borderRadius: 999, marginTop: 16,
                                background: 'var(--s-fill-hover,rgba(0,0,0,.07))', overflow: 'hidden', position: 'relative',
                            }}>
                                <div style={{
                                    position: 'absolute', height: '100%', width: '40%', background: color,
                                    borderRadius: 999, animation: 'sco-slide 1.4s ease-in-out infinite',
                                }} />
                            </div>
                        )}

                        {/* Selesai: ringkasan ruang dikembalikan */}
                        {stage === 'done' && reclaimedMB !== undefined && (
                            <div style={{
                                marginTop: 14, padding: '11px 13px', borderRadius: 'var(--s-r,10px)',
                                background: soft, fontSize: 13.5, color: INK, fontWeight: 600,
                            }}>
                                Ruang dikembalikan: {reclaimedMB} MB
                            </div>
                        )}

                        {/* Amaran jangan tutup (peringkat aktif) */}
                        {!isFinal && !isConfirm && (
                            <p style={{
                                margin: '14px 0 0', fontSize: 12, color: INK3,
                                textAlign: 'center', fontStyle: 'italic',
                            }}>
                                Jangan tutup tetingkap atau refresh halaman…
                            </p>
                        )}
                    </div>

                    {/* ===== Footer butang ===== */}
                    {(isFinal || isConfirm) && (
                        <div style={{
                            padding: '12px 22px', display: 'flex', alignItems: 'center',
                            justifyContent: 'flex-end', gap: 8,
                            background: 'var(--s-card-foot,#F6F7F9)', borderTop: `1px solid ${LINE}`,
                        }}>
                            {isConfirm ? (
                                <>
                                    <button onClick={onClose} style={btnQuiet}>Batal</button>
                                    <button onClick={onConfirm} style={btnStop}>Padam Sekarang</button>
                                </>
                            ) : (
                                <button onClick={onClose} style={btnPrimary}>Tutup</button>
                            )}
                        </div>
                    )}
                </Motion.div>
            </div>

            <style>{`
                @keyframes sco-spin  { to { transform: rotate(360deg); } }
                @keyframes sco-slide { 0% { left: -40%; } 100% { left: 100%; } }
            `}</style>
        </AnimatePresence>
    );
};

export default StorageCleanupOverlay;
